import { useAskInputSources } from '#pplx/frontend/apps/ask/ask-input/ask-input-attributions/hooks/useAskInputSources.js';
import { usePaywallUpsell } from '#pplx/frontend/apps/ask/hooks/pricing-hooks/usePaywallUpsell.js';
import { useSourceLimits } from '#pplx/frontend/apps/ask/sources/hooks/useSourceLimits.js';
import { useIsSubscribed } from '#pplx/frontend/auth/useIsSubscribed.js';
import { ModalOrigin } from '#pplx/frontend/components/types/types.js';
import { logger } from '#pplx/frontend/logger/browser.js';
import { useMemo } from 'react';
const ENABLED_SOURCES = new Set([
    'statista_mcp_cashmere',
    'wiley',
    'wiley_mcp_cashmere',
    'pitchbook_mcp_cashmere',
    'cbinsights_mcp_cashmere',
]);
/**
 * Detects when a user has exactly 1 remaining use for any source and provides upgrade functionality.
 * Used to display warnings before a source quota is completely exhausted.
 *
 * @returns Source warning with upgrade callback, or undefined if no sources are approaching their limit.
 * If multiple sources have 1 remaining use, returns the first one found (which is acceptable behavior).
 * The upgrade callback is omitted for users on the max subscription tier.
 */
export function useAskInputSourceLimitWarning({ isFollowUp, }) {
    const { isMax } = useIsSubscribed();
    const { getSourceLimit } = useSourceLimits({ enabled: true });
    const openPaywallUpsell = usePaywallUpsell();
    const { plusMenuSourcesProps } = useAskInputSources({
        enabled: true,
        isFollowUp,
    });
    const availableSources = useMemo(() => plusMenuSourcesProps.sources ?? [], [plusMenuSourcesProps.sources]);
    const sourcesApproachingLimit = useMemo(() => availableSources.filter((source) => ENABLED_SOURCES.has(source) && getSourceLimit(source).remaining === 1), [availableSources, getSourceLimit]);
    return useMemo(() => {
        // Just use the first source if multiple are approaching limit
        const sourceApproachingLimit = sourcesApproachingLimit.at(0);
        if (sourceApproachingLimit) {
            logger.info(`Detected source approaching limit`, {
                sourceApproachingLimit,
                sourcesApproachingLimit,
                isMax,
            });
        }
        if (!sourceApproachingLimit) {
            return undefined;
        }
        if (isMax) {
            return {
                source: sourceApproachingLimit,
            };
        }
        return {
            source: sourceApproachingLimit,
            upgrade() {
                openPaywallUpsell({
                    origin: ModalOrigin.SIDEBAR_BOTTOM_POPOVER,
                });
            },
        };
    }, [isMax, openPaywallUpsell, sourcesApproachingLimit]);
}
//# sourceMappingURL=useAskInputSourceLimitWarning.js.map